import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, type Empresa, type UsuarioEmpresa } from "@/lib/supabase";

type Perfil = { usuario: UsuarioEmpresa; empresa: Empresa } | { inactivo: true } | null;

type Ctx = {
  session: Session | null;
  /** undefined = cargando; null = usuario sin empresa (onboarding); {inactivo} = dado de baja */
  perfil: Exclude<Perfil, { inactivo: true }> | undefined;
  inactivo: boolean;
  perfilError: string | null;
  esAdmin: boolean;
  recargarPerfil: () => Promise<void>;
  salir: () => Promise<void>;
};

const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const qc = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((evento, s) => {
      setSession(s);
      if (evento === "SIGNED_OUT") qc.clear();
      // Tras un refresh de token, las queries que fallaron con 401 se reintentan solas.
      if (evento === "TOKEN_REFRESHED") qc.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  const uid = session?.user.id;
  const perfilQ = useQuery({
    queryKey: ["perfil", uid],
    enabled: !!uid,
    queryFn: async (): Promise<Perfil> => {
      const { data: u, error } = await supabase.from("usuarios_empresa").select("*").eq("usuario_id", uid!).maybeSingle();
      if (error) throw error;
      if (!u) return null;
      if (!u.activo) return { inactivo: true };
      const { data: e, error: e2 } = await supabase.from("empresas").select("*").eq("id", u.empresa_id).single();
      if (e2) throw e2;
      return { usuario: u, empresa: e };
    },
  });

  const datos = perfilQ.data && !("inactivo" in perfilQ.data) ? perfilQ.data : null;
  const valor = useMemo<Ctx>(() => ({
    session: session ?? null,
    perfil: session === undefined ? undefined : !session ? null : (perfilQ.isLoading || perfilQ.isError) ? undefined : datos,
    inactivo: !!perfilQ.data && "inactivo" in perfilQ.data,
    perfilError: perfilQ.isError ? "No pude cargar tu perfil. Revisa tu conexión." : null,
    esAdmin: !!datos && (datos.usuario.rol === "dueno" || datos.usuario.rol === "admin"),
    recargarPerfil: async () => { await qc.invalidateQueries({ queryKey: ["perfil"] }); },
    salir: async () => { await supabase.auth.signOut(); },
  }), [session, perfilQ.data, perfilQ.isLoading, perfilQ.isError, datos, qc]);

  return <AuthCtx.Provider value={valor}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth fuera de AuthProvider");
  return ctx;
}
